import { Input } from "@/shared/components/Input";
import { Switch } from "@/shared/components/Switch";
import type { MinisterioParaEscala } from "@/modules/escala/services/escalaService";

export interface ValoresEscala {
  ministerioId: string;
  nome: string;
  descricao: string;
  data: string;
  hora: string;
  exigeConfirmacao: boolean;
}

interface CamposEscalaProps {
  valores: ValoresEscala;
  onAtualizar: <K extends keyof ValoresEscala>(campo: K, valor: ValoresEscala[K]) => void;
  /**
   * Presente = criação: mostra o ministério e "Exigir confirmação", que não
   * mudam depois de criada (ministério dono e `exige_confirmacao` são
   * imutáveis — Documento de Requisitos, cap. 5). Ausente = edição.
   */
  criacao?: { ministerios: MinisterioParaEscala[]; carregando: boolean };
}

/**
 * Campos de dados da escala — os mesmos na criação (página "Nova escala") e
 * na edição (modal), para a escala ter um só formulário (G05). Componente
 * controlado: quem o usa guarda os valores e decide o que fazer ao salvar.
 *
 * DEC-04/S1: nenhuma antecedência mínima de Eventos — `escala_criar` aceita
 * qualquer `data_hora` (passada, presente ou futura).
 */
export function CamposEscala({ valores, onAtualizar, criacao }: CamposEscalaProps) {
  const semMinisterioDisponivel = !!criacao && !criacao.carregando && criacao.ministerios.length === 0;

  return (
    <div className="flex flex-col gap-4">
      {criacao && (
        <div className="flex flex-col gap-1.5">
          <label htmlFor="escala-ministerio" className="text-sm font-medium tracking-wide text-text">
            Ministério
          </label>
          {semMinisterioDisponivel ? (
            <p className="rounded-md border border-border bg-surface-2 px-3 py-2.5 text-xs text-muted">
              Você não lidera nem gerencia nenhum ministério ativo no momento — só ministérios ativos podem
              receber novas escalas.
            </p>
          ) : (
            <select
              id="escala-ministerio"
              value={valores.ministerioId}
              onChange={(e) => onAtualizar("ministerioId", e.target.value)}
              required
              disabled={criacao.carregando}
              className="w-full rounded-md border border-transparent bg-surface-2 px-4 py-2.5 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-60"
            >
              <option value="" disabled>
                {criacao.carregando ? "Carregando..." : "Selecione um ministério"}
              </option>
              {criacao.ministerios.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.nome}
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      <Input
        label="Nome da escala"
        name="nome"
        value={valores.nome}
        onChange={(e) => onAtualizar("nome", e.target.value)}
        placeholder="Ex.: Culto de domingo — manhã"
        required
        maxLength={120}
      />

      <div className="flex flex-col gap-1.5">
        <label htmlFor="escala-descricao" className="text-sm font-medium tracking-wide text-text">
          Descrição <span className="font-normal text-muted">(opcional)</span>
        </label>
        <textarea
          id="escala-descricao"
          value={valores.descricao}
          onChange={(e) => onAtualizar("descricao", e.target.value)}
          rows={3}
          className="w-full resize-none rounded-md border border-transparent bg-surface-2 px-4 py-2.5 text-sm text-text placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Input
          label="Data"
          type="date"
          name="data"
          value={valores.data}
          onChange={(e) => onAtualizar("data", e.target.value)}
          required
        />
        <Input
          label="Hora"
          type="time"
          name="hora"
          value={valores.hora}
          onChange={(e) => onAtualizar("hora", e.target.value)}
          required
        />
      </div>

      {criacao && (
        <div className="flex items-center justify-between gap-3 rounded-md border border-border bg-surface-2 px-3 py-2.5">
          <div>
            <p className="text-sm font-medium text-text">Exigir confirmação dos participantes</p>
            <p className="text-xs text-muted">
              {valores.exigeConfirmacao
                ? "Cada participante precisa confirmar a própria presença."
                : "Participantes entram já confirmados automaticamente (sem esta etapa)."}{" "}
              Não pode ser alterado depois de criar a escala.
            </p>
          </div>
          <Switch
            checked={valores.exigeConfirmacao}
            onChange={(v) => onAtualizar("exigeConfirmacao", v)}
            label="Exigir confirmação dos participantes"
          />
        </div>
      )}
    </div>
  );
}
